import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/Button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../ui/Card';
import { Skeleton } from '../ui/Skeleton';
import { X, Download, Loader2, User, Mail, Clock, Building2, FileText } from 'lucide-react';
import { toast } from 'react-toastify';
import { getPresencasDetalhesBySessao, exportPresencasPdf, exportPresencasXlsx } from '../../services/api';

function SessaoCheckinsModal({ isOpen, onClose, sessaoId, sessaoNome }) {
    const [checkins, setCheckins] = useState([]);
    const [loadingCheckins, setLoadingCheckins] = useState(true);
    const [errorCheckins, setErrorCheckins] = useState('');
    const [exportingPdf, setExportingPdf] = useState(false);
    const [exportingXlsx, setExportingXlsx] = useState(false);

    const fetchCheckins = useCallback(async () => {
        if (!sessaoId) return;

        setLoadingCheckins(true);
        setErrorCheckins('');
        try {
            const data = await getPresencasDetalhesBySessao(sessaoId);
            // Garante que 'data' é um array antes de definir o estado
            setCheckins(Array.isArray(data) ? data : []); 
        } catch (err) {
            console.error('Erro ao buscar detalhes de check-ins:', err);
            setErrorCheckins(err.message || 'Não foi possível carregar os check-ins.');
            toast.error(err.message || 'Erro ao carregar check-ins.');
            setCheckins([]); // Em caso de erro, garante que seja um array vazio
        } finally {
            setLoadingCheckins(false);
        }
    }, [sessaoId]);

    useEffect(() => {
        if (isOpen && sessaoId) {
            fetchCheckins();
        } else if (!isOpen) {
            // Limpar estados quando o modal fecha
            setCheckins([]);
            setErrorCheckins('');
            setLoadingCheckins(true);
        }
    }, [isOpen, sessaoId, fetchCheckins]);

    if (!isOpen) {
        return null;
    }

    const handleExportPdf = async () => {
        setExportingPdf(true);
        try {
            const blob = await exportPresencasPdf(sessaoId);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `checkins_sessao_${sessaoNome.replace(/\s+/g, '_')}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success('Lista de check-ins exportada para PDF com sucesso!');
        } catch (err) {
            console.error('Erro ao exportar PDF:', err);
            toast.error(`Erro ao exportar PDF: ${err.message}`);
        } finally {
            setExportingPdf(false);
        }
    };

    const handleExportXlsx = async () => {
        setExportingXlsx(true);
        try {
            const blob = await exportPresencasXlsx(sessaoId);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `checkins_sessao_${sessaoNome.replace(/\s+/g, '_')}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success('Lista de check-ins exportada para XLSX com sucesso!');
        } catch (err) {
            console.error('Erro ao exportar XLSX:', err);
            toast.error(`Erro ao exportar XLSX: ${err.message}`);
        } finally {
            setExportingXlsx(false);
        }
    };

    const CheckinsListSkeleton = () => (
        <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, index) => (
                <div key={index} className="flex items-center p-3 bg-muted/50 rounded-md">
                    <Skeleton className="h-8 w-8 rounded-full" />
                    <div className="ml-3 flex-grow space-y-2">
                        <Skeleton className="h-4 w-3/4" />
                        <Skeleton className="h-3 w-1/2" />
                    </div>
                    <Skeleton className="h-4 w-20" />
                </div>
            ))}
        </div>
    );

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex justify-center items-center p-4">
            <Card className="w-full max-w-3xl relative max-h-[90vh] flex flex-col">
                <button onClick={onClose} className="absolute top-4 right-4 p-1 rounded-full hover:bg-accent transition-colors"> {/* Adicionado transition-colors */}
                    <X className="h-5 w-5" />
                </button>
                <CardHeader>
                    <CardTitle className="mb-2">Check-ins da Sessão: {sessaoNome}</CardTitle> {/* Adicionado mb-2 */}
                    <CardDescription className="leading-relaxed">Lista de participantes que fizeram check-in nesta sessão.</CardDescription> {/* Adicionado leading-relaxed */}
                </CardHeader>
                <CardContent className="flex-grow overflow-y-auto">
                    {loadingCheckins ? (
                        <CheckinsListSkeleton />
                    ) : errorCheckins ? (
                        <p className="text-destructive text-center py-8">{errorCheckins}</p>
                    ) : checkins.length === 0 ? (
                        <p className="text-muted-foreground text-center py-8">Nenhum participante fez check-in nesta sessão ainda.</p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="text-left">
                                    <tr className="border-b">
                                        <th className="p-2 font-medium">Participante</th>
                                        <th className="p-2 font-medium hidden sm:table-cell">Instituição</th>
                                        <th className="p-2 font-medium text-right">Hora do Check-in</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {checkins.map(checkin => (
                                        <tr key={checkin.idPresenca} className="border-b last:border-b-0 hover:bg-accent/50 even:bg-muted/50 transition-colors"> {/* Adicionado transition-colors */}
                                            <td className="p-2">
                                                <div className="font-medium flex items-center">
                                                    <User className="h-4 w-4 mr-2 text-muted-foreground" />
                                                    {checkin.nomeParticipante}
                                                </div>
                                                <div className="text-xs text-muted-foreground flex items-center mt-1">
                                                    <Mail className="h-3 w-3 mr-1.5" />
                                                    {checkin.emailParticipante}
                                                </div>
                                            </td>
                                            <td className="p-2 text-muted-foreground hidden sm:table-cell">
                                                <div className="flex items-center">
                                                    <Building2 className="h-4 w-4 mr-2 text-muted-foreground" />
                                                    {checkin.instituicaoParticipante || 'N/A'}
                                                </div>
                                            </td>
                                            <td className="p-2 text-right text-muted-foreground">
                                                <div className="flex items-center justify-end">
                                                    <Clock className="h-3 w-3 mr-1.5" />
                                                    {new Date(checkin.dataHoraRegistro).toLocaleTimeString('pt-BR')}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
                <CardFooter className="flex justify-end gap-2 border-t pt-4">
                    <Button variant="outline" onClick={handleExportPdf} disabled={loadingCheckins || checkins.length === 0 || exportingPdf || exportingXlsx}>
                        {exportingPdf ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
                        Exportar PDF
                    </Button>
                    <Button variant="outline" onClick={handleExportXlsx} disabled={loadingCheckins || checkins.length === 0 || exportingPdf || exportingXlsx}>
                        {exportingXlsx ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileText className="mr-2 h-4 w-4" />}
                        Exportar XLSX
                    </Button>
                </CardFooter>
            </Card>
        </div>
    );
}

export default SessaoCheckinsModal;